import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, writeBatch, collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import unifiedData from './data.js';
import dataScience from './data/dataScience.js';
import dataDivertissement from './data/dataDivertissement.js';
import dataSport from './data/dataSport.js';
import NobelPrize from './data/dataNobelPrize';
import dataLiterature from './data/dataLiterature.js';
import dataArt from './data/dataArt.js';
import dataGeography from './data/dataGeography';
import SpeedRunQuestion from './SpeedRunQuestion';
import Leaderboard from './Leaderboard';
import './SpeedRun.css';

// Function to remove accents from a string
const removeAccents = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Levenshtein distance function for fuzzy matching
const levenshteinDistance = (a, b) => {
  const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));
  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;
  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  return matrix[b.length][a.length];
};

// Définition des thèmes et sous-thèmes
const themesData = {
  Divers: { data: Object.values(unifiedData), subThemes: null },
  Géographie: {
    data: [
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Géographie') || q.themes.includes('Geographie')),
      ...Object.values(dataGeography).filter((q) => q.themes.includes('Géographie')),
    ],
    subThemes: ['Capitale', 'Drapeau', 'Monnaies'],
  },
  Histoire: {
    data: [
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Histoire')),
      ...Object.values(NobelPrize).filter((q) => q.themes.includes('Histoire')),
    ],
    subThemes: null,
  },
  Art: {
    data: [
      ...Object.values(dataArt),
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Art')),
    ],
    subThemes: ['Architecture', 'Sculpture', 'Peinture'],
  },
  'Littérature et Langues': {
    data: [
      ...Object.values(dataLiterature),
      ...Object.values(NobelPrize).filter((q) => q.themes.includes('Littérature')),
    ],
    subThemes: ['Littérature', 'Prix Goncourt'],
  },
  'Culture Pop': {
    data: Object.values(dataDivertissement),
    subThemes: ['Cinéma', 'Musique', 'Fort Boyard'],
  },
  'Science et Nature': {
    data: [
      ...Object.values(dataScience),
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Nature')),
      ...Object.values(NobelPrize).filter((q) => q.themes.includes('Science')),
    ],
    subThemes: ['Nature', 'Astronomie', 'Prix Nobel', 'Médaille Fields', 'Prix Turing', 'Animaux']
  },
  Sport: {
    data: Object.values(dataSport),
    subThemes: ['Football', 'Formule 1'],
  },
};

const themes = Object.keys(themesData);

const SpeedRun = () => {
  const [stage, setStage] = useState('parameters');
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedSubTheme, setSelectedSubTheme] = useState(null);
  const [mode, setMode] = useState('easy');
  const [questionCount, setQuestionCount] = useState(50);
  const [correctAnswerDelay, setCorrectAnswerDelay] = useState(1);
  const [incorrectAnswerDelay, setIncorrectAnswerDelay] = useState(1);
  const [randomQuestions, setRandomQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [incorrectQuestions, setIncorrectQuestions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [endTime, setEndTime] = useState(null);
  const [skipDelay, setSkipDelay] = useState(false);
  const [userId, setUserId] = useState(null);
  const [leaderboards, setLeaderboards] = useState({});
  const nextButtonRef = useRef(null);

  // Synchroniser les délais en mode expert
  useEffect(() => {
    if (mode === 'expert' || (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0)) {
      setCorrectAnswerDelay(0);
      setIncorrectAnswerDelay(0);
    }
  }, [mode, correctAnswerDelay, incorrectAnswerDelay]);

  // Update timer during quiz
  useEffect(() => {
    let timer;
    if (stage === 'quiz' && startTime) {
      console.log('[Timer] Début du timer, startTime:', startTime);
      timer = setInterval(() => {
        setCurrentTime((Date.now() - startTime) / 1000);
      }, 100);
    }
    return () => {
      clearInterval(timer);
      console.log('[Timer] Arrêt du timer');
    };
  }, [stage, startTime]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch leaderboards
  const fetchLeaderboards = useCallback(async () => {
    try {
      const leaderboardCollection = collection(db, 'leaderboard');
      const leaderboardDocs = await getDocs(leaderboardCollection);
      const newLeaderboards = {};
      leaderboardDocs.forEach((doc) => {
        newLeaderboards[doc.id] = doc.data().scores || [];
      });
      setLeaderboards(newLeaderboards);
    } catch (error) {
      console.error('[Leaderboard] Erreur lors de la récupération des leaderboards:', error);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboards();
  }, [fetchLeaderboards]);

  // Generate questions only when starting a new session
  useEffect(() => {
    if (!selectedTheme || (themesData[selectedTheme].subThemes && !selectedSubTheme) || stage !== 'quiz') {
      console.log('[useEffect Questions] Pas de génération, état:', { selectedTheme, selectedSubTheme, stage });
      return;
    }

    console.log('[useEffect Questions] Génération initiale des questions pour:', { selectedTheme, selectedSubTheme, mode });
    const data = themesData[selectedTheme].data;
    const quizQuestions = selectedSubTheme ? data.filter((q) => q.themes.includes(selectedSubTheme)) : data;
    const shuffledQuestions = quizQuestions.sort(() => Math.random() - 0.5).slice(0, Math.min(questionCount, quizQuestions.length));
    setRandomQuestions(shuffledQuestions);
    setIncorrectQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswers(new Array(shuffledQuestions.length).fill({ answer: undefined, isCorrect: null }));
    setStartTime(Date.now());
    setCurrentTime(0);
  }, [selectedTheme, selectedSubTheme, questionCount, mode, stage]);

  // Get effective mode for leaderboard
  const getEffectiveMode = useCallback(() => {
    if (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0) return 'expert';
    return mode;
  }, [mode, correctAnswerDelay, incorrectAnswerDelay]);

  // Get leaderboard key
  const getLeaderboardKey = useCallback(() => {
    const themePart = selectedTheme ? selectedTheme.replace(/ /g, '-') : 'general';
    const subThemePart = selectedSubTheme ? `-${selectedSubTheme.replace(/ /g, '-')}` : '';
    return `speedrun-${getEffectiveMode()}-${questionCount}-${themePart}${subThemePart}`;
  }, [getEffectiveMode, questionCount, selectedTheme, selectedSubTheme]);

  // Update leaderboard
  const updateLeaderboard = useCallback(async (time) => {
    if (!userId) return false;
    try {
      const batch = writeBatch(db);
      const leaderboardKey = getLeaderboardKey();
      const leaderboardRef = doc(db, 'leaderboard', leaderboardKey);
      const leaderboardSnap = await getDoc(leaderboardRef);

      let leaderboardData = leaderboardSnap.exists() ? leaderboardSnap.data().scores || [] : [];
      const userIndex = leaderboardData.findIndex((entry) => entry.userId === userId);
      const lastTimeInLeaderboard = leaderboardData.length === 10 ? leaderboardData[9].score : Infinity;
      const userBestTime = userIndex !== -1 ? leaderboardData[userIndex].score : Infinity;

      console.log('[Leaderboard] Vérification:', { time, userBestTime, lastTimeInLeaderboard });
      if (time >= userBestTime && time >= lastTimeInLeaderboard) {
        console.log('[Leaderboard] Temps insuffisant pour mise à jour');
        return false;
      }

      const displayName = auth.currentUser?.displayName || 'Utilisateur';
      const pseudo = displayName.split(' ')[0] || 'Utilisateur';
      const now = new Date().toISOString();
      if (userIndex !== -1) {
        if (time < userBestTime) {
          leaderboardData[userIndex] = { score: time, userId, pseudo, lastUpdated: now };
        }
      } else {
        leaderboardData.push({ score: time, userId, pseudo, lastUpdated: now });
      }

      leaderboardData.sort((a, b) => a.score - b.score);
      leaderboardData = leaderboardData.slice(0, 10);

      batch.set(leaderboardRef, { scores: leaderboardData });
      await batch.commit();
      setLeaderboards((prev) => ({ ...prev, [leaderboardKey]: leaderboardData }));
      console.log('[Leaderboard] Mise à jour réussie pour', leaderboardKey, 'avec temps:', time);
      return true;
    } catch (error) {
      console.error('[Leaderboard] Erreur lors de la mise à jour:', error);
      return false;
    }
  }, [userId, getLeaderboardKey]);

  const handleThemeChange = useCallback((theme) => {
    console.log('[handleThemeChange] Changement de thème:', theme);
    setSelectedTheme(theme);
    const subThemes = themesData[theme].subThemes;
    if (!subThemes || subThemes.length === 0) {
      setSelectedSubTheme(null);
      setStage('quiz');
    } else {
      setSelectedSubTheme(null);
      setStage('subthemes');
    }
  }, []);

  const handleSubThemeChange = useCallback((subTheme) => {
    console.log('[handleSubThemeChange] Changement de sous-thème ou thème entier:', subTheme);
    if (subTheme === 'Thème entier') {
      setSelectedSubTheme(null);
    } else {
      setSelectedSubTheme(subTheme);
    }
    setStage('quiz');
  }, []);

  const handleBackToParameters = useCallback(() => {
    console.log('[handleBackToParameters] Retour aux paramètres');
    setSelectedTheme(null);
    setSelectedSubTheme(null);
    setRandomQuestions([]);
    setIncorrectQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setStage('parameters');
    setStartTime(null);
    setCurrentTime(0);
    setEndTime(null);
    setSkipDelay(false);
  }, []);

  const handleBackToThemes = useCallback(() => {
    console.log('[handleBackToThemes] Retour aux thèmes');
    setSelectedSubTheme(null);
    setRandomQuestions([]);
    setIncorrectQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setStage('themes');
    setStartTime(null);
    setCurrentTime(0);
    setEndTime(null);
    setSkipDelay(false);
  }, []);

  const nextQuestion = useCallback(() => {
    if (currentQuestionIndex + 1 < randomQuestions.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (incorrectQuestions.length > 0) {
      console.log('[nextQuestion] Redémarrage avec les questions incorrectes:', incorrectQuestions);
      setRandomQuestions(incorrectQuestions);
      setIncorrectQuestions([]);
      setCurrentQuestionIndex(0);
      setSelectedAnswers(new Array(incorrectQuestions.length).fill({ answer: undefined, isCorrect: null }));
    } else {
      console.log('[nextQuestion] Fin du speed run');
      const timeTaken = (Date.now() - startTime) / 1000;
      setEndTime(timeTaken);
      updateLeaderboard(timeTaken);
      setStage('results');
    }
    setSkipDelay(false);
  }, [currentQuestionIndex, randomQuestions, incorrectQuestions, startTime, updateLeaderboard]);

  const handleAnswerClick = useCallback(
    (answerIndex, directAnswer = '') => {
      const currentQuestion = randomQuestions[currentQuestionIndex];
      let isCorrect;
      if (mode === 'easy') {
        isCorrect = answerIndex === currentQuestion.correctAnswer;
      } else {
        const normalizedUserAnswer = removeAccents(directAnswer.trim().toLowerCase());
        const normalizedCorrectAnswer = removeAccents(String(currentQuestion.answers[currentQuestion.correctAnswer]).trim().toLowerCase());
        const maxEditDistance = 2;
        const distance = levenshteinDistance(normalizedUserAnswer, normalizedCorrectAnswer);
        const maxLength = Math.max(normalizedUserAnswer.length, normalizedCorrectAnswer.length);
        const similarityThreshold = 0.8;
        isCorrect = distance <= maxEditDistance && (1 - distance / maxLength) >= similarityThreshold;
      }

      console.log('[handleAnswerClick] Réponse sélectionnée:', {
        answerIndex,
        directAnswer,
        correctAnswer: currentQuestion.correctAnswer,
        correctAnswerText: currentQuestion.answers[currentQuestion.correctAnswer],
        isCorrect,
        mode,
        distance: mode !== 'easy' ? levenshteinDistance(removeAccents(directAnswer.trim().toLowerCase()), removeAccents(String(currentQuestion.answers[currentQuestion.correctAnswer]).trim().toLowerCase())) : null,
      }, 'Question:', currentQuestionIndex + 1);

      const updatedAnswers = [...selectedAnswers];
      updatedAnswers[currentQuestionIndex] = { answer: mode === 'easy' ? answerIndex : directAnswer, isCorrect };
      setSelectedAnswers(updatedAnswers);

      if (!isCorrect) {
        setIncorrectQuestions([...incorrectQuestions, currentQuestion]);
      }

      if (skipDelay) {
        nextQuestion();
      } else if (mode === 'expert' || (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0)) {
        setTimeout(() => nextQuestion(), correctAnswerDelay * 1000);
      } else {
        const delay = isCorrect ? correctAnswerDelay : incorrectAnswerDelay;
        setTimeout(() => nextQuestion(), delay * 1000);
      }
      // Mettre le focus sur le bouton "Question Suivante" après la réponse
      if (nextButtonRef.current) {
        nextButtonRef.current.focus();
      }
    },
    [currentQuestionIndex, randomQuestions, incorrectQuestions, mode, selectedAnswers, nextQuestion, correctAnswerDelay, incorrectAnswerDelay, skipDelay]
  );

  const handleSkipDelay = useCallback(() => {
    console.log('[handleSkipDelay] Passage immédiat à la question suivante');
    setSkipDelay(true);
    nextQuestion();
  }, [nextQuestion]);

  const handleStartSpeedRun = useCallback(() => {
    console.log('[handleStartSpeedRun] Lancement du speed run');
    setStage('themes');
  }, []);

  const handleRetakeSpeedRun = useCallback(() => {
    console.log('[handleRetakeSpeedRun] Reprise du speed run');
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setIncorrectQuestions([]);
    setEndTime(null);
    const data = themesData[selectedTheme].data;
    const quizQuestions = selectedSubTheme ? data.filter((q) => q.themes.includes(selectedSubTheme)) : data;
    const shuffledQuestions = quizQuestions.sort(() => Math.random() - 0.5).slice(0, Math.min(questionCount, quizQuestions.length));
    setRandomQuestions(shuffledQuestions);
    setStartTime(Date.now());
    setCurrentTime(0);
    setStage('quiz');
    setSkipDelay(false);
  }, [selectedTheme, selectedSubTheme, questionCount]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000).toString().padStart(3, '0');
    return `${minutes}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  const progressPercentage = randomQuestions.length > 0 ? ((currentQuestionIndex + 1) / randomQuestions.length) * 100 : 0;

  const otherThemes = themes.filter((theme) => theme !== 'Divers');
  const chunkThemes = (array, size) => {
    const chunked = [];
    for (let i = 0; i < array.length; i += size) {
      chunked.push(array.slice(i, i + size));
    }
    return chunked;
  };

  const modeOptions = [
    { value: 'easy', label: 'Facile (QCM)' },
    { value: 'difficult', label: 'Difficile (Réponse directe)' },
    { value: 'expert', label: 'Expert (Pas de feedback)' },
  ];
  const questionCountOptions = [50, 100];
  const delayOptions = [0, 1, 2, 5];

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && stage === 'quiz' && selectedAnswers[currentQuestionIndex]?.answer !== undefined && !skipDelay) {
      handleSkipDelay();
    }
  }, [stage, currentQuestionIndex, selectedAnswers, handleSkipDelay, skipDelay]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  return (
    <div className="quizz-container" onKeyDown={handleKeyPress}>
      {stage === 'parameters' ? (
        <div className="parameters-selection">
          <h1>Paramètres du SpeedRun</h1>
          <div className="config-selection">
            <div>
              <label>Mode</label>
              <div className="button-group-speedrun mode-buttons">
                {modeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setMode(option.value)}
                    className={`config-button ${mode === option.value ? 'selected' : ''}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label>Nombre de questions</label>
              <div className="button-group-speedrun">
                {questionCountOptions.map((count) => (
                  <button
                    key={count}
                    onClick={() => setQuestionCount(count)}
                    className={`config-button ${questionCount === count ? 'selected' : ''}`}
                  >
                    {count} Questions
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label>Délai après une bonne réponse (secondes)</label>
              <div className="button-group-speedrun">
                {delayOptions.map((delay) => (
                  <button
                    key={delay}
                    onClick={() => setCorrectAnswerDelay(delay)}
                    className={`config-button ${correctAnswerDelay === delay ? 'selected' : ''} ${mode === 'expert' || (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0) ? 'disabled' : ''}`}
                    disabled={mode === 'expert' || (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0)}
                  >
                    {delay}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label>Délai après une mauvaise réponse (secondes)</label>
              <div className="button-group-speedrun">
                {delayOptions.map((delay) => (
                  <button
                    key={delay}
                    onClick={() => setIncorrectAnswerDelay(delay)}
                    className={`config-button ${incorrectAnswerDelay === delay ? 'selected' : ''} ${mode === 'expert' || (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0) ? 'disabled' : ''}`}
                    disabled={mode === 'expert' || (mode === 'difficult' && correctAnswerDelay === 0 && incorrectAnswerDelay === 0)}
                  >
                    {delay}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={handleStartSpeedRun}
              className="start-button"
            >
              Choisir un thème
            </button>
          </div>
        </div>
      ) : stage === 'themes' ? (
        <div className="theme-selection">
          <div className="theme-header">
            <button onClick={handleBackToParameters} className="back-button">
              ← Retour aux paramètres
            </button>
            <h1>Choisir un thème</h1>
          </div>
          <div className="general-theme">
            <div
              onClick={() => handleThemeChange('Divers')}
              className="theme-card theme-general"
            >
              <h2>Divers</h2>
            </div>
          </div>
          <div className="other-themes">
            {chunkThemes(otherThemes, 3).map((row, rowIdx) => (
              <div key={rowIdx} className={`theme-row ${row.length < 3 ? 'center-last' : ''}`}>
                {row.map((theme, idx) => {
                  const themeIndex = rowIdx * 3 + idx;
                  return (
                    <div
                      key={theme}
                      onClick={() => handleThemeChange(theme)}
                      className={`theme-card theme-${themeIndex}`}
                    >
                      <h2>{theme}</h2>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : stage === 'subthemes' && themesData[selectedTheme].subThemes && themesData[selectedTheme].subThemes.length > 0 ? (
        <div className="subtheme-selection">
          <div className="theme-header">
            <button onClick={handleBackToThemes} className="back-button">
              ← Retour aux thèmes
            </button>
            <h1>{selectedTheme}</h1>
          </div>
          <div className="subthemes-list">
            <div className="subtheme-row centered">
              <div
                key="Thème entier"
                onClick={() => handleSubThemeChange('Thème entier')}
                className="subtheme-card theme-0"
              >
                <h3>Thème entier <br /> {selectedTheme}</h3>
              </div>
            </div>
            {chunkThemes(themesData[selectedTheme].subThemes, 3).map((row, rowIdx) => (
              <div key={rowIdx} className={`subtheme-row ${row.length < 3 ? 'center-last' : ''}`}>
                {row.map((subTheme, idx) => {
                  const subThemeIndex = rowIdx * 3 + idx + 1;
                  return (
                    <div
                      key={subTheme}
                      onClick={() => handleSubThemeChange(subTheme)}
                      className={`subtheme-card theme-${subThemeIndex % 10}`}
                    >
                      <h3>{subTheme}</h3>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <Leaderboard leaderboard={leaderboards[getLeaderboardKey()] || []} userId={userId} isTimeBased={true} />
        </div>
      ) : stage === 'quiz' && randomQuestions.length > 0 ? (
        <div className={`theme-content theme-${themes.indexOf(selectedTheme) % 10}`}>
          <div className="theme-header">
            <button onClick={handleBackToThemes} className="back-button">
              ← Retour aux thèmes
            </button>
            <h1>{selectedSubTheme || selectedTheme} - {mode.charAt(0).toUpperCase() + mode.slice(1)}</h1>
          </div>
          <div className="content">
            <div className="progress-info">
              <p className="question-counter">Question {currentQuestionIndex + 1}/{randomQuestions.length}</p>
              <p className="timer">Temps: {formatTime(currentTime)}</p>
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${progressPercentage}%` }} data-progress={progressPercentage}></div>
              </div>
              {selectedAnswers[currentQuestionIndex] && selectedAnswers[currentQuestionIndex].answer !== undefined && (
                <button
                  ref={nextButtonRef}
                  onClick={handleSkipDelay}
                  className="next-question-button"
                >
                  Question Suivante
                </button>
              )}
            </div>
            <SpeedRunQuestion
              key={`${currentQuestionIndex}-${randomQuestions[currentQuestionIndex].question}`}
              question={randomQuestions[currentQuestionIndex].question}
              answers={mode === 'easy' ? randomQuestions[currentQuestionIndex].answers : null}
              correctAnswer={randomQuestions[currentQuestionIndex].correctAnswer}
              onAnswerClick={handleAnswerClick}
              isLocked={selectedAnswers[currentQuestionIndex] && selectedAnswers[currentQuestionIndex].answer !== undefined}
              feedback={mode !== 'expert' && selectedAnswers[currentQuestionIndex] && selectedAnswers[currentQuestionIndex].answer !== undefined
                ? selectedAnswers[currentQuestionIndex].isCorrect
                  ? 'Correct'
                  : `La réponse est : ${randomQuestions[currentQuestionIndex].answers[randomQuestions[currentQuestionIndex].correctAnswer]}`
                : null}
              image={randomQuestions[currentQuestionIndex].image || null}
              sound={randomQuestions[currentQuestionIndex].sound || null}
              mode={mode}
              userAnswer={selectedAnswers[currentQuestionIndex]?.answer}
            />
            <Leaderboard leaderboard={leaderboards[getLeaderboardKey()] || []} userId={userId} isTimeBased={true} />
          </div>
        </div>
      ) : stage === 'results' ? (
        <div className={`theme-content theme-${themes.indexOf(selectedTheme) % 10}`}>
          <div className="theme-header">
            <button onClick={handleBackToThemes} className="back-button">
              ← Retour aux thèmes
            </button>
            <h1>{selectedSubTheme || selectedTheme} - Résultats</h1>
          </div>
          <div className="content">
            <div className="results">
              <h2>Temps total : {formatTime(endTime)}</h2>
              <p>Votre score : {selectedAnswers.filter(a => a.isCorrect).length} / {randomQuestions.length}</p>
              <Leaderboard leaderboard={leaderboards[getLeaderboardKey()] || []} userId={userId} isTimeBased={true} />
              <button onClick={handleRetakeSpeedRun} className="retake-button">
                Recommencer
              </button>
              <Link to="/account" className="subscribe-button">
                S'abonner
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="loading-questions">Chargement des questions...</div>
      )}
    </div>
  );
};

export default React.memo(SpeedRun);